import type {
    Form,
    FormField,
    FormSection,
    ReactiveDropdownCatalogPhase,
    ReactiveFormSupportDataPhase,
    ReactiveFormSupportDataSource,
} from '../../interfaces/form.interface';
import { InputType } from '../../interfaces/input.interface';
import {
    normalizeReactiveDropdownCatalogs,
    type NormalizedReactiveDropdownCatalogs,
} from '../dropdown/reactive-dropdown-catalogs';

export type ReactiveFormLoadMilestone =
    'shellReady' |
    'recordReady' |
    'criticalReady' |
    'hydratedReady' |
    'interactiveReady' |
    'completeReady';

export interface ReactiveFormLoadPolicy {
    readonly autoClassifyDropdowns?: boolean;
    readonly requiredDropdownsAreCritical?: boolean;
    readonly visibleDropdownsAreCritical?: boolean;
    readonly hiddenDropdownsAreLazy?: boolean;
    readonly dependentDropdownsAreDeferred?: boolean;
    readonly lazyDataSourcesStayLazy?: boolean;
    readonly previousDataDropdownsAreCritical?: boolean;
    readonly visiblePreviousDataDropdownsOverrideExplicitPhase?: boolean;
    readonly defaultDropdownPhase?: ReactiveDropdownCatalogPhase;
    readonly defaultSupportDataPhase?: ReactiveFormSupportDataPhase;
    readonly completeRequiresLazyCatalogs?: boolean;
}

export interface ReactiveFormLoadOrchestratorOptions<TModel> {
    readonly previousData?: Partial<TModel> | null;
    readonly policy?: ReactiveFormLoadPolicy;
}

export interface ReactiveFormLoadPlanItem {
    readonly name: string;
    readonly reason: ReactiveFormLoadPlanReason;
}

export type ReactiveFormLoadPlanReason =
    'explicit' |
    'required' |
    'visible' |
    'hidden' |
    'dependent' |
    'lazyDataSource' |
    'previousData' |
    'default';

export interface ReactiveFormSupportDataLoadPlanItem {
    readonly key: string;
    readonly reason: 'explicit' | 'default';
}

export interface ReactiveFormLoadPhasePlan {
    readonly critical: readonly ReactiveFormLoadPlanItem[];
    readonly deferred: readonly ReactiveFormLoadPlanItem[];
    readonly lazy: readonly ReactiveFormLoadPlanItem[];
}

export interface ReactiveFormSupportDataLoadPhasePlan {
    readonly critical: readonly ReactiveFormSupportDataLoadPlanItem[];
    readonly deferred: readonly ReactiveFormSupportDataLoadPlanItem[];
    readonly lazy: readonly ReactiveFormSupportDataLoadPlanItem[];
}

export interface ReactiveFormLoadDiagnostics {
    readonly dropdownCount: number;
    readonly criticalDropdownCount: number;
    readonly deferredDropdownCount: number;
    readonly lazyDropdownCount: number;
    readonly supportDataCount: number;
    readonly criticalSupportDataCount: number;
    readonly deferredSupportDataCount: number;
    readonly lazySupportDataCount: number;
    readonly explicitDropdownCount: number;
    readonly inferredDropdownCount: number;
}

export interface ReactiveFormLoadPlan {
    readonly dropdowns: ReactiveFormLoadPhasePlan;
    readonly supportData: ReactiveFormSupportDataLoadPhasePlan;
    readonly milestones: readonly ReactiveFormLoadMilestone[];
    readonly diagnostics: ReactiveFormLoadDiagnostics;
}

export interface ReactiveFormLoadReadinessInput {
    readonly shellReady?: boolean;
    readonly recordReady?: boolean;
    readonly criticalCatalogsReady?: boolean;
    readonly criticalSupportDataReady?: boolean;
    readonly hydrationReady?: boolean;
    readonly deferredCatalogsReady?: boolean;
    readonly deferredSupportDataReady?: boolean;
    readonly lazyCatalogsReady?: boolean;
    readonly minTimeExhausted?: boolean;
    readonly hasBlockingErrors?: boolean;
}

export interface ReactiveFormLoadReadinessSnapshot {
    readonly shellReady: boolean;
    readonly recordReady: boolean;
    readonly criticalReady: boolean;
    readonly hydratedReady: boolean;
    readonly interactiveReady: boolean;
    readonly completeReady: boolean;
    readonly blockingErrors: boolean;
}

interface ResolvedDropdownPhase {
    readonly phase: ReactiveDropdownCatalogPhase;
    readonly reason: ReactiveFormLoadPlanReason;
}

const DEFAULT_LOAD_POLICY: Required<ReactiveFormLoadPolicy> = {
    autoClassifyDropdowns: true,
    requiredDropdownsAreCritical: true,
    visibleDropdownsAreCritical: true,
    hiddenDropdownsAreLazy: true,
    dependentDropdownsAreDeferred: true,
    lazyDataSourcesStayLazy: true,
    previousDataDropdownsAreCritical: true,
    visiblePreviousDataDropdownsOverrideExplicitPhase: true,
    defaultDropdownPhase: 'critical',
    defaultSupportDataPhase: 'deferred',
    completeRequiresLazyCatalogs: false,
};

const DROPDOWN_INPUT_TYPES = new Set<InputType>([
    InputType.DROPDOWN,
    InputType.DROPDOWN_SEARCH,
    InputType.MULTISELECT,
    InputType.MULTISELECT_SEARCH,
]);

const LOAD_MILESTONES: readonly ReactiveFormLoadMilestone[] = [
    'shellReady',
    'recordReady',
    'criticalReady',
    'hydratedReady',
    'interactiveReady',
    'completeReady',
];

/** Builds a deterministic load plan so hosts do not need manual build(), pool() or timeout gates. */
export class ReactiveFormLoadOrchestrator {
    public static createPlan<TModel>(
        form: Form<TModel>,
        options: ReactiveFormLoadOrchestratorOptions<TModel> = {},
    ): ReactiveFormLoadPlan {
        const policy = ReactiveFormLoadOrchestrator.resolvePolicy(options.policy);
        const explicitCatalogs = normalizeReactiveDropdownCatalogs(form.dropdownCatalogs);
        const dropdowns = ReactiveFormLoadOrchestrator.createEmptyDropdownPlan();
        const supportData = ReactiveFormLoadOrchestrator.createEmptySupportDataPlan();
        const dropdownFields = ReactiveFormLoadOrchestrator.resolveDropdownFields(form);
        let explicitDropdownCount = 0;

        for (const field of dropdownFields) {
            const resolvedPhase = ReactiveFormLoadOrchestrator.resolveDropdownPhase(field, explicitCatalogs, policy, options.previousData ?? null);
            dropdowns[resolvedPhase.phase].push({
                name: field.name,
                reason: resolvedPhase.reason,
            });

            if (resolvedPhase.reason === 'explicit') {
                explicitDropdownCount += 1;
            }
        }

        for (const source of form.supportDataSources ?? []) {
            const phase = source.phase ?? policy.defaultSupportDataPhase;
            const reason = source.phase ? 'explicit' : 'default';

            supportData[phase].push({
                key: source.key,
                reason,
            });
        }

        return {
            dropdowns: ReactiveFormLoadOrchestrator.freezeDropdownPlan(dropdowns),
            supportData: ReactiveFormLoadOrchestrator.freezeSupportDataPlan(supportData),
            milestones: LOAD_MILESTONES,
            diagnostics: ReactiveFormLoadOrchestrator.createDiagnostics(dropdowns, supportData, explicitDropdownCount),
        };
    }

    public static snapshotReadiness(
        input: ReactiveFormLoadReadinessInput,
        policy: ReactiveFormLoadPolicy = {},
    ): ReactiveFormLoadReadinessSnapshot {
        const resolvedPolicy = ReactiveFormLoadOrchestrator.resolvePolicy(policy);
        const blockingErrors = input.hasBlockingErrors === true;
        const shellReady = input.shellReady === true;
        const recordReady = shellReady && input.recordReady === true;
        const criticalReady = recordReady && input.criticalCatalogsReady === true && input.criticalSupportDataReady !== false && !blockingErrors;
        const hydratedReady = criticalReady && input.hydrationReady === true;
        const interactiveReady = hydratedReady && input.minTimeExhausted !== false;
        const deferredReady = input.deferredCatalogsReady !== false && input.deferredSupportDataReady !== false;
        const lazyReady = !resolvedPolicy.completeRequiresLazyCatalogs || input.lazyCatalogsReady === true;
        const completeReady = interactiveReady && deferredReady && lazyReady;

        return {
            shellReady,
            recordReady,
            criticalReady,
            hydratedReady,
            interactiveReady,
            completeReady,
            blockingErrors,
        };
    }

    public static resolvePolicy(policy: ReactiveFormLoadPolicy = {}): Required<ReactiveFormLoadPolicy> {
        return {
            ...DEFAULT_LOAD_POLICY,
            ...policy,
        };
    }

    private static resolveDropdownPhase<TModel>(
        field: FormField<TModel>,
        explicitCatalogs: NormalizedReactiveDropdownCatalogs,
        policy: Required<ReactiveFormLoadPolicy>,
        previousData: Partial<TModel> | null,
    ): ResolvedDropdownPhase {
        const explicitPhase = ReactiveFormLoadOrchestrator.resolveExplicitPhase(field.name, explicitCatalogs);

        if (
            policy.autoClassifyDropdowns
            && ReactiveFormLoadOrchestrator.shouldPromoteVisiblePreviousDataDropdown(
                field,
                explicitPhase,
                explicitCatalogs,
                policy,
                previousData,
            )
        ) {
            return {
                phase: 'critical',
                reason: 'previousData',
            };
        }

        if (explicitPhase) {
            return {
                phase: explicitPhase,
                reason: 'explicit',
            };
        }

        if (!policy.autoClassifyDropdowns) {
            return {
                phase: field.dataSource?.lazy === true ? 'lazy' : policy.defaultDropdownPhase,
                reason: field.dataSource?.lazy === true ? 'lazyDataSource' : 'default',
            };
        }

        if (policy.previousDataDropdownsAreCritical && ReactiveFormLoadOrchestrator.hasPreviousDataValue(field.name, previousData)) {
            return {
                phase: 'critical',
                reason: 'previousData',
            };
        }

        if (policy.lazyDataSourcesStayLazy && field.dataSource?.lazy === true) {
            return {
                phase: 'lazy',
                reason: 'lazyDataSource',
            };
        }

        if (policy.hiddenDropdownsAreLazy && field.conditions.isVisible === false) {
            return {
                phase: 'lazy',
                reason: 'hidden',
            };
        }

        if (policy.requiredDropdownsAreCritical && field.conditions.isRequired) {
            return {
                phase: 'critical',
                reason: 'required',
            };
        }

        if (policy.dependentDropdownsAreDeferred && ReactiveFormLoadOrchestrator.hasDropdownDependencies(field)) {
            return {
                phase: 'deferred',
                reason: 'dependent',
            };
        }

        if (policy.visibleDropdownsAreCritical && field.conditions.isVisible) {
            return {
                phase: 'critical',
                reason: 'visible',
            };
        }

        return {
            phase: policy.defaultDropdownPhase,
            reason: 'default',
        };
    }

    private static resolveExplicitPhase(
        fieldName: string,
        catalogs: NormalizedReactiveDropdownCatalogs,
    ): ReactiveDropdownCatalogPhase | null {
        if (catalogs.critical.includes(fieldName)) {
            return 'critical';
        }

        if (catalogs.deferred.includes(fieldName)) {
            return 'deferred';
        }

        if (catalogs.lazy.includes(fieldName)) {
            return 'lazy';
        }

        return null;
    }

    private static resolveDropdownFields<TModel>(form: Form<TModel>): FormField<TModel>[] {
        return [...form.sections]
            .sort((left, right) => left.order - right.order)
            .flatMap((section) => ReactiveFormLoadOrchestrator.resolveSectionDropdownFields(section));
    }

    private static resolveSectionDropdownFields<TModel>(section: FormSection<TModel>): FormField<TModel>[] {
        return [...section.fields]
            .filter((field) => ReactiveFormLoadOrchestrator.isDropdownField(field))
            .sort((left, right) => left.order - right.order);
    }

    private static isDropdownField<TModel>(field: FormField<TModel>): boolean {
        return DROPDOWN_INPUT_TYPES.has(field.input.type);
    }

    private static hasPreviousDataValue<TModel>(fieldName: string, previousData: Partial<TModel> | null): boolean {
        if (!previousData) {
            return false;
        }

        const value = (previousData as Record<string, unknown>)[fieldName];

        return value !== null && value !== undefined && value !== '';
    }

    private static shouldPromoteVisiblePreviousDataDropdown<TModel>(
        field: FormField<TModel>,
        explicitPhase: ReactiveDropdownCatalogPhase | null,
        explicitCatalogs: NormalizedReactiveDropdownCatalogs,
        policy: Required<ReactiveFormLoadPolicy>,
        previousData: Partial<TModel> | null,
    ): boolean {
        return policy.visiblePreviousDataDropdownsOverrideExplicitPhase
            && explicitPhase === 'lazy'
            && field.conditions.isVisible !== false
            && ReactiveFormLoadOrchestrator.hasPreviousDataValue(field.name, previousData)
            && ReactiveFormLoadOrchestrator.requiresCatalogForPreviousDataLabel(field, explicitCatalogs);
    }

    private static requiresCatalogForPreviousDataLabel<TModel>(
        field: FormField<TModel>,
        explicitCatalogs: NormalizedReactiveDropdownCatalogs,
    ): boolean {
        return field.dataSource !== undefined
            || ReactiveFormLoadOrchestrator.resolveExplicitPhase(field.name, explicitCatalogs) !== null
            || !ReactiveFormLoadOrchestrator.hasHydratedDropdownOptions(field);
    }

    private static hasHydratedDropdownOptions<TModel>(field: FormField<TModel>): boolean {
        const inputData = field.input.data as { readonly values?: readonly unknown[] } | null | undefined;
        const values = inputData?.values;

        return Array.isArray(values) && values.length > 0;
    }

    private static hasDropdownDependencies<TModel>(field: FormField<TModel>): boolean {
        return (field.dataSource?.dependsOn?.length ?? 0) > 0 || (field.dependencies?.length ?? 0) > 0;
    }

    private static createEmptyDropdownPlan(): Record<ReactiveDropdownCatalogPhase, ReactiveFormLoadPlanItem[]> {
        return {
            critical: [],
            deferred: [],
            lazy: [],
        };
    }

    private static createEmptySupportDataPlan(): Record<ReactiveFormSupportDataPhase, ReactiveFormSupportDataLoadPlanItem[]> {
        return {
            critical: [],
            deferred: [],
            lazy: [],
        };
    }

    private static freezeDropdownPlan(
        dropdowns: Record<ReactiveDropdownCatalogPhase, ReactiveFormLoadPlanItem[]>,
    ): ReactiveFormLoadPhasePlan {
        return {
            critical: Object.freeze([...dropdowns.critical]),
            deferred: Object.freeze([...dropdowns.deferred]),
            lazy: Object.freeze([...dropdowns.lazy]),
        };
    }

    private static freezeSupportDataPlan(
        supportData: Record<ReactiveFormSupportDataPhase, ReactiveFormSupportDataLoadPlanItem[]>,
    ): ReactiveFormSupportDataLoadPhasePlan {
        return {
            critical: Object.freeze([...supportData.critical]),
            deferred: Object.freeze([...supportData.deferred]),
            lazy: Object.freeze([...supportData.lazy]),
        };
    }

    private static createDiagnostics(
        dropdowns: Record<ReactiveDropdownCatalogPhase, ReactiveFormLoadPlanItem[]>,
        supportData: Record<ReactiveFormSupportDataPhase, ReactiveFormSupportDataLoadPlanItem[]>,
        explicitDropdownCount: number,
    ): ReactiveFormLoadDiagnostics {
        const dropdownCount = dropdowns.critical.length + dropdowns.deferred.length + dropdowns.lazy.length;
        const supportDataCount = supportData.critical.length + supportData.deferred.length + supportData.lazy.length;

        return {
            dropdownCount,
            criticalDropdownCount: dropdowns.critical.length,
            deferredDropdownCount: dropdowns.deferred.length,
            lazyDropdownCount: dropdowns.lazy.length,
            supportDataCount,
            criticalSupportDataCount: supportData.critical.length,
            deferredSupportDataCount: supportData.deferred.length,
            lazySupportDataCount: supportData.lazy.length,
            explicitDropdownCount,
            inferredDropdownCount: dropdownCount - explicitDropdownCount,
        };
    }
}

export type ReactiveFormSupportDataSourceLoadResolver<TModel = unknown> = ReactiveFormSupportDataSource<TModel>['load'];
